import { useCallback } from 'react';
import { Box } from '@mui/material';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';
import { getDialogTabbables, getIntakeIntro } from './dialogFocus';
import { INTAKE_INTRO_ID } from './intake-form/intake-copy';

export const MESSAGE_INPUT_ID = 'chat-message-input';

interface SkipToMessageInputLinkProps {
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /** While the intake form is pending there is no message input; skip to the form instead. */
  intakePending?: boolean;
}

/**
 * WCAG 2.4.1 Bypass Blocks — first focusable control inside the chat dialog
 * lets keyboard users skip header chrome and jump straight to the message field.
 *
 * Uses a ref instead of document.getElementById because the widget renders
 * inside a shadow root where ID lookup on document does not reach the input.
 */
export function SkipToMessageInputLink({
  inputRef,
  intakePending = false,
}: SkipToMessageInputLinkProps) {
  const { t, locale } = useWidgetI18n();
  const activateSkip = useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      const link = e.currentTarget;
      const dialog = link.closest<HTMLElement>('[role="dialog"]');
      const intro = intakePending && dialog ? getIntakeIntro(dialog) : null;
      if (intro) {
        // Focus on the tabIndex=-1 sentence is what screen readers read.
        intro.focus();
        return;
      }
      const input = intakePending ? null : inputRef.current;
      if (input) {
        input.focus();
        return;
      }
      // Never a silent no-op: fall back to the first control after this link.
      const fallback = dialog
        ? getDialogTabbables(dialog).find((el) => el !== link)
        : undefined;
      fallback?.focus();
    },
    [inputRef, intakePending],
  );

  return (
    <Box
      component="a"
      href={`#${intakePending ? INTAKE_INTRO_ID : MESSAGE_INPUT_ID}`}
      // The intake skip text is English in every catalog for now.
      lang={intakePending ? 'en' : locale}
      onClick={activateSkip}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          activateSkip(e);
        }
      }}
      sx={{
        position: 'absolute',
        left: -10000,
        top: 0,
        zIndex: 2000,
        p: 1.5,
        m: 1,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        typography: 'body2',
        fontWeight: 600,
        textDecoration: 'none',
        borderRadius: 1,
        clipPath: 'inset(100%)',
        overflow: 'hidden',
        width: 1,
        height: 1,
        '&:focus': {
          left: 8,
          top: 8,
          width: 'auto',
          height: 'auto',
          clipPath: 'none',
          outline: '2px solid',
          outlineOffset: 2,
          outlineColor: 'primary.dark',
        },
      }}
    >
      {t(intakePending ? 'drawer.skipToIntakeForm' : 'drawer.skipToMessageInput')}
    </Box>
  );
}
